import { DynDadosCadastraisProps } from ".";
import { Autocomplete, FormControl, FormHelperText, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { validarCpfCnpj } from "../../../../helpers/verificadores";
import { formatarCEP, formatarCPFCNPJ, formatarTelefone, permitirApenasNumerosOuSN } from "../../../../helpers/masks";
import moment from "moment";
import BasicDatePicker from "../../../BasicDatePicker";
import { useMemo } from "react";
import { useQuery } from "react-query";
import axiosClient from "../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import { desformatarCEP } from "../../../../helpers/unmask";
import { ViaCEPResposta } from "../../../../types/externas/viacep-resultado";
import SearchIcon from '@mui/icons-material/Search';
import SelectNacionalidade from "./SelectNacionalidade";

export default function DadosConjuge({ spec, extras }: DynDadosCadastraisProps) {
    const { control, getFieldState, watch, getValues, setValue } = useFormContext();

    const ufs = useMemo(() => ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"], [])

    //queries
    const { data } = useQuery({
        queryKey: ["pesquisar-cidades", watch(`${spec.name}.uf`)],
        queryFn: async () => {
            if (watch(`${spec.name}.uf`) === undefined) return []
            let result = await axiosClient.get("/pesquisar-cidades", {
                params: {
                    uf: getValues(`${spec.name}.uf`)
                }
            })
            return result?.data
        }
    });
    const { dataCidades }: ICidades = {
        dataCidades: data || []
    }
    //

    //funções manipuladoras
    const handlePesquisarCep = async () => {
        const { cep_conjuge } = getValues(`${spec.name}`)
        if (cep_conjuge === undefined || cep_conjuge === "") return

        const unmaskCep = desformatarCEP(cep_conjuge)

        if (unmaskCep.length !== 8) {
            enqueueSnackbar('CEP inválido. Deve conter 8 dígitos.', { variant: 'error' })
            return
        }

        try {
            const response = await axiosClient.get<ViaCEPResposta>(`https://viacep.com.br/ws/${unmaskCep}/json/`);
            const { bairro, logradouro, localidade, uf } = response.data
            setValue(`${spec.name}.uf_conjuge`, uf)
            setValue(`${spec.name}.bairro_conjuge`, bairro)
            setValue(`${spec.name}.cidade_conjuge`, localidade)
            setValue(`${spec.name}.endereco_conjuge`, logradouro)
        } catch (error) {
            enqueueSnackbar('Erro ao buscar endereço por CEP: ' + error, { variant: 'error' })
            return
        }
    }

    const possuiConjuge = watch(`${spec.name}.possui_conjuge`) === "Sim"
    return (
        <Grid container size={{ xs: 12 }} spacing={2}>
            <Grid size={{ xs: 12 }}>
                <Typography fontWeight={'bold'} variant="h6">Dados do Cônjuge/Companheiro(a)</Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
                <Typography fontWeight={'bold'} >Dados básicos</Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                    name={`${spec.name}.nome_conjuge`}
                    control={control}
                    rules={{ required: "Campo obrigatório" }}
                    render={({ field, fieldState: { error, invalid } }) => {
                        return (
                            <TextField
                                {...field}
                                required
                                error={invalid}
                                label="Nome"
                                fullWidth
                                value={field.value}
                                onChange={(e) => field.onChange(e.target.value)}
                                slotProps={{ htmlInput: { maxLength: 40 } }}
                                helperText={error ? error.message : ""}
                            />
                        );
                    }}
                />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                    name={`${spec.name}.nome_social_conjuge`}
                    control={control}
                    render={({ field, fieldState: { error, invalid } }) => {
                        return (
                            <TextField
                                {...field}
                                error={invalid}
                                label="Nome Social"
                                fullWidth
                                value={field.value}
                                onChange={(e) => field.onChange(e.target.value)}
                                slotProps={{ htmlInput: { maxLength: 40 } }}
                                helperText={error ? error.message : ""}
                            />
                        );
                    }}
                />
            </Grid>
            <Grid size={{ xs: 3 }}>
                <Controller
                    name={`${spec.name}.rg_conjuge`}
                    control={control}
                    rules={{ required: "Campo obrigatório" }}
                    render={({ field, fieldState: { error, invalid } }) => (
                        <TextField
                            {...field}
                            required
                            label='RG'
                            type="text"
                            fullWidth
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            error={invalid}
                            helperText={error ? error.message : ""}
                            slotProps={{ htmlInput: { maxLength: 20 } }}
                        />
                    )}
                />
            </Grid>
            <Grid size={{ xs: 3 }}>
                <Controller
                    name={`${spec.name}.cpf_conjuge`}
                    control={control}
                    rules={{
                        required: "Campo obrigatório",
                        validate: (value) => {
                            if (!possuiConjuge) return true;
                            return validarCpfCnpj(value) || 'CPF inválido';
                        },
                    }}
                    render={({ field, fieldState: { error, invalid } }) => {
                        return (
                            <TextField
                                {...field}
                                required
                                error={invalid}
                                label="CPF"
                                fullWidth
                                value={field.value}
                                onChange={(e) => field.onChange(formatarCPFCNPJ(e.target.value))}
                                slotProps={{ htmlInput: { maxLength: 17 } }}
                                helperText={error ? error.message : ""}
                            />
                        );
                    }}
                />
            </Grid>
            <Grid size={{ xs: 3 }}>
                <Controller
                    name={`${spec.name}.telefone_conjuge`}
                    control={control}
                    rules={{
                        maxLength: { value: 20, message: 'Telefone deve ter no máximo 20 caracteres' }
                    }}
                    render={({ field, fieldState: { error, invalid } }) => (
                        <TextField
                            {...field}
                            label="Telefone"
                            placeholder="Telefone"
                            fullWidth
                            value={field.value}
                            onChange={(e) => field.onChange(formatarTelefone(e.target.value))}
                            error={invalid}
                            helperText={error?.message || ""}
                            slotProps={{ htmlInput: { maxLength: 20 } }}
                        />
                    )}
                />
            </Grid>
            <Grid size={{ xs: 3 }}>
                <Controller
                    name={`${spec.name}.sexo_conjuge`}
                    control={control}
                    rules={{ required: "Campo obrigatório" }}
                    render={({ field, fieldState: { error, invalid } }) => {
                        return (
                            <FormControl fullWidth error={invalid} required>
                                <InputLabel id="sexo-select">Sexo</InputLabel>
                                <Select
                                    {...field}
                                    labelId="sexo-select"
                                    value={field.value}
                                    label="Sexo"
                                >
                                    <MenuItem value="M">Masculino</MenuItem>
                                    <MenuItem value="F">Feminino</MenuItem>
                                </Select>
                                <FormHelperText error>{error ? error.message : ""}</FormHelperText>
                            </FormControl>
                        );
                    }}
                />
            </Grid>
            <Grid size={{ xs: 6 }}>
                <Controller
                    name={`${spec.name}.nome_mae_conjuge`}
                    control={control}
                    rules={{ required: "Campo obrigatório" }}
                    render={({ field, fieldState: { error, invalid } }) => {
                        return (
                            <TextField
                                {...field}
                                required
                                error={invalid}
                                label="Nome da Mãe"
                                fullWidth
                                value={field.value}
                                onChange={(e) => field.onChange(e.target.value)}
                                helperText={error ? error.message : ""}
                            />
                        );
                    }}
                />
            </Grid>
            <Grid size={{ xs: 6 }}>
                <Controller
                    name={`${spec.name}.nome_pai_conjuge`}
                    control={control}
                    render={({ field }) => {
                        return (
                            <TextField
                                {...field}
                                label="Nome do Pai"
                                fullWidth
                                value={field.value}
                                onChange={(e) => field.onChange(e.target.value)}
                            />
                        );
                    }}
                />
            </Grid>
            <Grid size={{ xs: 6 }}>
                <Controller
                    name={`${spec.name}.data_nascimento_conjuge`}
                    control={control}
                    rules={{
                        required: 'Campo obrigatório',
                        validate: (value) => {
                            const dataMoment = moment(value, "YYYY-MM-DD", true);
                            if (!dataMoment.isValid()) return "Data inválida";
                            if (dataMoment.isAfter(moment())) return "Data não pode ser futura";
                            return true;
                        }
                    }}
                    render={({ field, fieldState: { error, invalid } }) => (
                        <BasicDatePicker
                            required
                            label="Data de Nascimento"
                            helperText={error ? error.message : ""}
                            error={invalid}
                            value={field.value ? moment(field.value, "YYYY-MM-DD") : null}
                            blockFutureDates
                            futureDates={moment().subtract(1, 'days')}
                            onChange={(data) => {
                                field.onChange(data ? moment(data).format("YYYY-MM-DD") : null);
                            }}
                            formatString="DD/MM/YYYY"
                            readonly={false}
                        />
                    )}
                />
            </Grid>
            <Grid size={{ xs: 6 }}>
                <Controller
                    name={`${spec.name}.nacionalidade_conjuge`}
                    control={control}
                    rules={{ required: 'A nacionalidade do procurador é obrigatória' }}
                    render={({ field, fieldState: { error, invalid } }) => (
                        <SelectNacionalidade
                            required
                            value={field.value}
                            onChange={(value) => field.onChange(value)}
                            error={invalid}
                            errorMessage={error ? error.message : undefined}
                        />
                    )}
                />
            </Grid>
            <Grid container size={{ xs: 12 }} spacing={2}>
                <Grid size={{ xs: 12 }}>
                    <Typography fontWeight={'bold'}>Endereço</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Controller
                        name={`${spec.name}.cep_conjuge`}
                        control={control}
                        rules={{ required: 'Campo obrigatório' }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="CEP"
                                variant="outlined"
                                defaultValue={""}
                                fullWidth
                                required
                                onChange={(e) => field.onChange(formatarCEP(e.target.value))}
                                onBlur={handlePesquisarCep}
                                error={getFieldState(`${spec.name}.cep`).invalid}
                                helperText={getFieldState(`${spec.name}.cep`).error?.message || ""}
                                slotProps={{
                                    input: {
                                        endAdornment: <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={handlePesquisarCep}
                                                edge="end"
                                            >
                                                <SearchIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                }}
                            />
                        )}
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                        name={`${spec.name}.endereco_conjuge`}
                        control={control}
                        rules={{ required: 'Campo obrigatório' }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="Endereço"
                                variant="outlined"
                                required
                                fullWidth
                                error={getFieldState(`${spec.name}.endereco`).invalid}
                                helperText={getFieldState(`${spec.name}.endereco`).error?.message || ""}
                                slotProps={{
                                    inputLabel: { shrink: field.value ? true : undefined }
                                }}
                            />
                        )}
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 2 }}>
                    <Controller
                        name={`${spec.name}.numero_conjuge`}
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="Número"
                                type="text"
                                variant="outlined"
                                defaultValue={""}
                                fullWidth
                                onChange={(e) => {
                                    field.onChange(permitirApenasNumerosOuSN(e.target.value))
                                }}
                                error={getFieldState(`${spec.name}.numero`).invalid}
                                helperText={getFieldState(`${spec.name}.numero`).error?.message || ""}
                            />
                        )}
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                    <Controller
                        name={`${spec.name}.complemento_conjuge`}
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="Complemento"
                                defaultValue={""}
                                variant="outlined"
                                fullWidth
                                error={getFieldState(`${spec.name}.complemento`).invalid}
                                helperText={getFieldState(`${spec.name}.complemento`).error?.message || ""}
                            />
                        )}
                    />
                </Grid>


                <Grid size={{ xs: 12, md: 3 }}>
                    <Controller
                        name={`${spec.name}.bairro_conjuge`}
                        control={control}
                        rules={{ required: 'Campo obrigatório' }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="Bairro"
                                variant="outlined"
                                required
                                fullWidth
                                error={getFieldState(`${spec.name}.bairro`).invalid}
                                helperText={getFieldState(`${spec.name}.bairro`).error?.message || ""}
                                slotProps={{
                                    inputLabel: { shrink: field.value ? true : undefined }
                                }}
                            />
                        )}
                    />
                </Grid>


                <Grid size={{ xs: 12, md: 2 }}>
                    <Controller
                        name={`${spec.name}.uf_conjuge`}
                        control={control}
                        rules={{ required: 'Campo obrigatório' }}
                        render={({ field }) => (
                            <FormControl fullWidth error={getFieldState(`${spec.name}.uf`).invalid} required>
                                <InputLabel id="uf-conjuge-select">UF</InputLabel>
                                <Select
                                    labelId="uf-conjuge-select"
                                    value={field.value || ''}
                                    label="UF"
                                    onChange={(e) => field.onChange(e.target.value)}
                                >
                                    {ufs.map((uf, index) => (
                                        <MenuItem key={index} value={uf}>{uf}</MenuItem>
                                    ))}
                                </Select>
                                {getFieldState(`${spec.name}.uf`).error && (
                                    <FormHelperText>{getFieldState(`${spec.name}.uf`).error?.message}</FormHelperText>
                                )}
                            </FormControl>
                        )}
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <Controller
                        name={`${spec.name}.cidade_conjuge`}
                        control={control}
                        rules={{ required: 'Campo obrigatório' }}
                        render={({ field }) => (
                            <>
                                <Autocomplete
                                    value={field.value || null}
                                    fullWidth
                                    disablePortal
                                    options={dataCidades}
                                    defaultValue={""}
                                    onChange={(_, v) => field.onChange(v)}
                                    renderInput={(params) =>
                                        <TextField
                                            {...params}
                                            label="Cidade"
                                            required
                                            defaultValue={""}
                                            error={Boolean(getFieldState(`${spec.name}.cidade`).invalid)}
                                            helperText={getFieldState(`${spec.name}.cidade`).error?.message || ""}
                                        />
                                    }
                                />
                            </>
                        )}
                    />
                </Grid>
            </Grid>
        </Grid>
    )
}

interface ICidades {
    dataCidades: Array<string>
}
